/**
 * Project Template Selector - Choose and create projects from templates
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { PROJECT_TEMPLATES, ProjectTemplateType, ProjectTemplate } from '../types/templates';
import { Project } from '../types';
import { Check, ChevronRight, ArrowLeft } from 'lucide-react';
import { generateId } from '../utils/idGenerator';

interface ProjectTemplateSelectorProps {
  onProjectCreate: (project: Partial<Project>, template: ProjectTemplate) => void;
  onCancel: () => void;
  organizationId: string;
}

export default function ProjectTemplateSelector({ 
  onProjectCreate, 
  onCancel,
  organizationId 
}: ProjectTemplateSelectorProps) {
  const [step, setStep] = useState<'select' | 'configure'>('select');
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);
  
  // Form state
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [targetEndDate, setTargetEndDate] = useState('');
  const [totalBudget, setTotalBudget] = useState('');

  const handleTemplateSelect = (template: ProjectTemplate) => {
    setSelectedTemplate(template);
    setStep('configure');
  };

  const handleBack = () => {
    if (step === 'configure') {
      setStep('select');
      setSelectedTemplate(null);
    } else {
      onCancel();
    }
  };

  const handleCreate = () => {
    if (!selectedTemplate || !projectName) return;

    const newProject: Partial<Project> = {
      id: generateId('proj', projectName),
      name: projectName,
      description: projectDescription,
      organizationId,
      status: 'Active',
      startDate: startDate || new Date().toISOString().split('T')[0],
      targetEndDate: targetEndDate || '',
      // Store template metadata
      metadata: {
        templateType: selectedTemplate.type,
        templateId: selectedTemplate.id,
      },
      costs: totalBudget ? selectedTemplate.budgetTracking.categories?.map(cat => ({
        id: cat.id,
        category: cat.name,
        amount: cat.allocatedAmount || 0,
        description: cat.description || '',
      })) : [],
    };

    onProjectCreate(newProject, selectedTemplate);
  };

  if (step === 'select') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Choose a Project Template</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Select a template that best fits your project type
            </p>
          </div>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.values(PROJECT_TEMPLATES).map((template) => (
            <Card
              key={template.id}
              className="cursor-pointer hover:shadow-lg hover:border-primary/40 transition-all group"
              onClick={() => handleTemplateSelect(template)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="text-4xl mb-2">{template.icon}</div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <CardTitle className="text-lg">{template.name}</CardTitle>
                <CardDescription className="text-sm">
                  {template.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">Features:</div>
                    <div className="flex flex-wrap gap-1">
                      {template.budgetTracking.enabled && (
                        <Badge variant="outline" className="text-xs">Budget Tracking</Badge>
                      )}
                      {template.workflowStages && (
                        <Badge variant="outline" className="text-xs">
                          {template.workflowStages.length} Stages
                        </Badge>
                      )}
                      {template.taskTemplates && template.taskTemplates.length > 0 && (
                        <Badge variant="outline" className="text-xs">
                          {template.taskTemplates.length} Templates
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">Task Types:</div>
                    <div className="flex flex-wrap gap-1">
                      {template.taskCategories.slice(0, 3).map((cat) => (
                        <Badge key={cat} variant="secondary" className="text-xs">
                          {cat.replace('-', ' ')}
                        </Badge>
                      ))}
                      {template.taskCategories.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{template.taskCategories.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Configure step
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <span className="text-3xl">{selectedTemplate?.icon}</span>
            Configure {selectedTemplate?.name}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {selectedTemplate?.description}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Project Details</CardTitle>
          <CardDescription>
            Basic information about your project
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Project Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Payment Processing Platform"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe your project..."
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              rows={4}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="endDate">Target End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={targetEndDate}
                onChange={(e) => setTargetEndDate(e.target.value)}
              />
            </div>

            {selectedTemplate?.budgetTracking.enabled && (
              <div className="space-y-2">
                <Label htmlFor="budget">Total Budget (optional)</Label>
                <Input
                  id="budget"
                  type="number"
                  placeholder="e.g., 150000"
                  value={totalBudget}
                  onChange={(e) => setTotalBudget(e.target.value)}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Template Configuration Preview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Task Statuses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {selectedTemplate?.defaultStatuses.map((status) => (
                <Badge key={status} variant="outline">
                  {status}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Task Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {selectedTemplate?.taskCategories.map((category) => (
                <Badge key={category} variant="secondary">
                  {category.replace('-', ' ')}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {selectedTemplate?.workflowStages && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Workflow Stages</CardTitle>
            <CardDescription>
              Your project will be organized into these stages
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {selectedTemplate.workflowStages.map((stage, idx) => (
                <div key={stage.id} className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm bg-${stage.color}-500`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{stage.name}</div>
                    <div className="text-sm text-muted-foreground">{stage.description}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {stage.taskCategories.map((cat) => (
                        <Badge key={cat} variant="outline" className="text-xs">
                          {cat.replace('-', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {selectedTemplate?.budgetTracking.enabled && selectedTemplate.budgetTracking.categories && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Budget Categories</CardTitle>
            <CardDescription>
              Default budget breakdown (you can customize this later)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {selectedTemplate.budgetTracking.categories.map((category) => (
                <div key={category.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <div className="font-medium">{category.name}</div>
                    {category.description && (
                      <div className="text-xs text-muted-foreground">{category.description}</div>
                    )}
                  </div>
                  {category.allocatedAmount && (
                    <Badge variant="outline">
                      ${category.allocatedAmount.toLocaleString()}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={handleBack}>
          Back to Templates
        </Button>
        <Button 
          onClick={handleCreate}
          disabled={!projectName}
          className="min-w-32"
        >
          <Check className="w-4 h-4 mr-2" />
          Create Project
        </Button>
      </div>
    </div>
  );
}
